/* eslint-disable max-classes-per-file */

import PaymentMode from '@/shared/enums/PaymentMode'
import CheckoutConfig from '@/shared/models/CheckoutConfig'
import Card from './Card'

class Pix {
  readonly expirationDate?: string

  constructor(config: CheckoutConfig) {
    this.expirationDate = config.expirationDate
  }
}

class BankSlip {
  readonly expirationDate?: string
  readonly line1?: string
  readonly line2?: string
  readonly line3?: string
  readonly logo?: string

  constructor(config: CheckoutConfig) {
    this.expirationDate = config.boletoExpirationDate || config.expirationDate
    this.line1 = config.boletoLine1
    this.line2 = config.boletoLine2
    this.line3 = config.boletoLine3
    this.logo = config.logo
  }
}

class Oxxo {
  readonly expirationDate?: string
  readonly description?: string
  readonly logo?: string

  constructor(config: CheckoutConfig) {
    this.expirationDate = config.expirationDate
    this.description = config.description
    this.logo = config.logo
  }
}

class TiendasYFarmacias {
  readonly expirationDate?: string
  readonly description?: string
  readonly logo?: string

  constructor(config: CheckoutConfig) {
    this.expirationDate = config.expirationDate
    this.description = config.description
    this.logo = config.logo
  }
}

class Spei {
  readonly description?: string
  readonly logo?: string

  constructor(config: CheckoutConfig) {
    this.description = config.description
    this.logo = config.logo
  }
}

class PayCash {
  readonly expirationDate?: string

  constructor(config: CheckoutConfig) {
    this.expirationDate = config.expirationDate
  }
}

class PicPay {
  readonly expirationDate?: string

  constructor(config: CheckoutConfig) {
    this.expirationDate = config.expirationDate
  }
}

class BankTransfer {
  readonly description?: string
  // Initialized so Vue 2 makes them reactive (the Itaú DEBIN field visibility depends on bankId)
  bankId: string | null = null
  debinAlias: string | null = null

  constructor(config: CheckoutConfig) {
    this.description = config.description
  }
}

class PagoEfectivo {
  readonly expirationDate?: string

  constructor(config: CheckoutConfig) {
    this.expirationDate = config.expirationDate
  }
}

class Deuna {
  readonly expirationDate?: string

  constructor(config: CheckoutConfig) {
    this.expirationDate = config.expirationDate
  }
}

class Nequi {
  readonly expirationDate?: string

  constructor(config: CheckoutConfig) {
    this.expirationDate = config.expirationDate
  }
}

class Mach {
  readonly expirationDate?: string

  constructor(config: CheckoutConfig) {
    this.expirationDate = config.expirationDate
  }
}

class QrInteroperable {
  readonly expirationDate?: string

  constructor(config: CheckoutConfig) {
    this.expirationDate = config.expirationDate
  }
}

class Breb {
  readonly expirationDate?: string

  constructor(config: CheckoutConfig) {
    this.expirationDate = config.expirationDate
  }
}

export default class Payment {
  method?: PaymentMode

  amount: number

  asset: string

  card?: Card

  pix?: Pix

  bankSlip?: BankSlip

  oxxo?: Oxxo

  tiendasYFarmacias?: TiendasYFarmacias

  spei?: Spei

  paycash?: PayCash

  picpay?: PicPay

  bankTransfer?: BankTransfer

  pagoEfectivo?: PagoEfectivo

  deuna?: Deuna

  nequi?: Nequi

  mach?: Mach

  qrInteroperable?: QrInteroperable

  breb?: Breb

  processPayment: boolean

  delayCapture: boolean

  saveCard: boolean

  pixDirect?: boolean

  softDescriptor?: string

  country: string

  currency: string

  constructor(config: CheckoutConfig) {
    this.method = config.method
    this.amount = config.amount
    this.asset = config.asset
    this.processPayment = config.processPayment
    this.delayCapture = config.delayCapture
    this.saveCard = config.saveCard
    this.pixDirect = config.pixDirect
    this.country = config.paymentCountry || 'BR'
    this.currency = config.paymentCurrency || 'BRL'
    this.softDescriptor = config.softDescriptor

    const isSubscribingWithCard = (
      config.subscription &&
      config.subscriptionScheme === 'CARD'
    )

    if (
      this.method === PaymentMode.creditCard ||
      this.method === PaymentMode.debitCard ||
      isSubscribingWithCard
    ) {
      this.card = new Card(config.cardInstallments)
    } else if (this.method === PaymentMode.pix) {
      this.pix = new Pix(config)
    } else if (this.method === PaymentMode.bankSlip) {
      this.bankSlip = new BankSlip(config)
    } else if (this.method === PaymentMode.oxxo) {
      this.oxxo = new Oxxo(config)
    } else if (this.method === PaymentMode.paynet || this.method === PaymentMode.tiendasYFarmacias) {
      this.tiendasYFarmacias = new TiendasYFarmacias(config)
    } else if (this.method === PaymentMode.spei) {
      this.spei = new Spei(config)
    } else if (this.method === PaymentMode.paycash) {
      this.paycash = new PayCash(config)
    } else if (this.method === PaymentMode.picpay) {
      this.picpay = new PicPay(config)
    } else if (this.method === PaymentMode.bankTransfer) {
      this.bankTransfer = new BankTransfer(config)
    } else if (this.method === PaymentMode.pagoEfectivo) {
      this.pagoEfectivo = new PagoEfectivo(config)
    } else if (this.method === PaymentMode.deuna) {
      this.deuna = new Deuna(config)
    } else if (this.method === PaymentMode.nequi) {
      this.nequi = new Nequi(config)
    } else if (this.method === PaymentMode.mach) {
      this.mach = new Mach(config)
    } else if (this.method === PaymentMode.qrInteroperable) {
      this.qrInteroperable = new QrInteroperable(config)
    } else if (this.method === PaymentMode.breb) {
      this.breb = new Breb(config)
    }
  }
}
